import { hasMainPageRestriction } from '../services/accessManagement/mainPageRestriction';
import { Link, Navigate, matchPath, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMenuChangeReturnPath } from '../services/menuChange/menuChangeLinks';
import { primaryText } from '../styling/stylingHelper';
import { useAuth } from '../services/authentication/authContext';
import { isInvoiceOnlyAccount, canAccessSuperAdminDashboard } from '../services/accessManagement/accessManagement';

/**
 * Routes an invoice-only account may reach. Beyond the three nav destinations
 * (see isInvoiceOnlyAccount) this covers the places the app itself still sends
 * them: an order's detail page, settings, the payment/pending-access flows and
 * the public auth/legal pages. Everything else redirects to /home.
 */
const ALLOWED_PATHS = [
  '/home',
  // Support has its own account/identity guard and is also available in invoice-only mode.
  '/support',
  '/support/*',
  '/setup-overview', // SetupOverviewRoute independently requires super-admin impersonation.
  '/invoices',
  '/orderhistory/orders',
  '/orderhistory/factuur/:factuurId',
  '/orderhistory/:orderId',
  '/settings',
  '/settings/legal/:docKey',
  '/profile',
  '/profile/referrals',
  '/userProfile',
  '/access',
  '/payment',
  '/success',
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/terms-and-conditions',
  '/privacy-policy',
  '/culinary-cloud-privacy-policy',
  '/cookies-policy',
];

/**
 * `/orderhistory/:orderId` above also pattern-matches its static sibling routes,
 * so the ones an invoice-only account must NOT reach are excluded explicitly.
 */
const BLOCKED_PATHS = ['/orderhistory/products'];

const isAllowedPath = (pathname: string): boolean => {
  if (BLOCKED_PATHS.some(pattern => matchPath(pattern, pathname))) return false;
  return ALLOWED_PATHS.some(pattern => matchPath(pattern, pathname));
};

/**
 * Wraps the whole route tree: an invoice-only account that lands anywhere outside
 * its allowlist (a bookmark, a deep link, the /dashboard the login flow hardcodes)
 * is sent to /home. Fails open — while the profile is still loading nobody is
 * invoice-only yet, so a normal account is never redirected.
 */
export function InvoiceOnlyRedirect({ children }: { children: JSX.Element }) {
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();
  const { pathname } = useLocation();

  if (hasMainPageRestriction(backendUser) || !isInvoiceOnlyAccount(backendUser, accountSettings)) return children;
  // Escape hatch: a super admin previewing invoice-only mode on their own account
  // must keep /admin reachable, or they can never remove the setting again.
  if (
    backendUser &&
    canAccessSuperAdminDashboard(backendUser) &&
    (matchPath('/admin', pathname) || matchPath('/admin/*', pathname))
  ) {
    return children;
  }
  if (isAllowedPath(pathname)) return children;
  if (getMenuChangeReturnPath(pathname)) return <div className={`p-6 ${primaryText}`}><p role="alert">{t('menuChange.links.accessDenied')}</p><Link to="/home" className="underline">{t('menuChange.links.back')}</Link></div>;
  return <Navigate to="/home" replace />;
}

/**
 * Gates the pages that only exist for invoice-only accounts (/home, /invoices);
 * everyone else goes to the dashboard. Waits for the profile to arrive first —
 * accountSettings load after `loading` flips, so deciding earlier would bounce an
 * invoice-only account off its own page on every refresh.
 */
export function InvoiceOnlyRoute({ children }: { children: JSX.Element }) {
  const { user, backendUser, accountSettings } = useAuth();

  if (user && !backendUser) {
    return <div className="p-6">Loading...</div>;
  }
  if (!isInvoiceOnlyAccount(backendUser, accountSettings)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}
